/**
 * Security Headers Utilities
 * 
 * Utilities for managing security headers, Content Security Policy,
 * and request validation across the application.
 */

import { getSecurityConfig, generateCSPString, isAllowedAPIEndpoint, getRequiredHeaders, logSecurityEvent } from '@/config/security';
import { generateUUID } from '@/utils/uuid';

/**
 * Apply security headers to document
 */
export const applySecurityHeaders = (): void => {
  const config = getSecurityConfig();
  
  // Apply CSP via meta tag (for client-side enforcement)
  const cspString = generateCSPString(config.csp);
  updateMetaTag('Content-Security-Policy', cspString);
  
  // Apply other security headers via meta tags
  Object.entries(config.headers).forEach(([header, value]) => {
    if (value) {
      updateMetaTag(`http-equiv-${header}`, value);
    }
  });

  logSecurityEvent('security_headers_applied', {
    cspDirectives: Object.keys(config.csp).length,
    environment: import.meta.env.PROD ? 'production' : 'development'
  });
};

/**
 * Update or create meta tag
 */
const updateMetaTag = (name: string, content: string): void => {
  let meta = document.querySelector(`meta[http-equiv="${name}"]`) as HTMLMetaElement;
  
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('http-equiv', name);
    document.head.appendChild(meta);
  }
  
  meta.setAttribute('content', content);
};

/**
 * Validate and sanitize request URL
 */
export const validateRequestURL = (url: string): { isValid: boolean; error?: string } => {
  try {
    const parsedURL = new URL(url);
    
    // Check protocol
    if (!['https:', 'http:'].includes(parsedURL.protocol)) {
      return { isValid: false, error: 'Invalid protocol' };
    }
    
    // In production, only allow HTTPS
    if (import.meta.env.PROD && parsedURL.protocol !== 'https:') {
      return { isValid: false, error: 'HTTPS required in production' };
    }
    
    // Check if endpoint is allowed
    if (!isAllowedAPIEndpoint(url)) {
      logSecurityEvent('blocked_api_endpoint', { url, hostname: parsedURL.hostname });
      return { isValid: false, error: 'Endpoint not whitelisted' };
    }
    
    return { isValid: true };
  } catch (_error) {
    return { isValid: false, error: 'Invalid URL format' };
  }
};

/**
 * Secure fetch wrapper with automatic security headers
 */
export const secureFetch = async (
  url: string, 
  options: RequestInit = {}
): Promise<Response> => {
  // Validate URL
  const urlValidation = validateRequestURL(url);
  if (!urlValidation.isValid) {
    throw new Error(`Security validation failed: ${urlValidation.error}`);
  }

  // Get required security headers
  const securityHeaders = getRequiredHeaders();
  
  // Merge headers
  const headers = {
    ...securityHeaders,
    ...(options.headers as Record<string, string> || {})
  };

  // Add timing headers for monitoring
  const requestId = generateUUID();
  headers['X-Request-ID'] = requestId;
  headers['X-Request-Start'] = Date.now().toString();

  const requestOptions: RequestInit = {
    ...options,
    headers,
    // Force credentials for cross-origin requests
    credentials: 'include',
  };

  try {
    const startTime = performance.now();
    const response = await fetch(url, requestOptions);
    const endTime = performance.now();

    // Log request metrics
    logSecurityEvent('api_request_completed', {
      requestId,
      url: new URL(url).pathname, // Don't log full URL for privacy
      method: options.method || 'GET',
      status: response.status,
      duration: endTime - startTime,
      success: response.ok
    });

    // Check for security-related status codes
    if (response.status === 403) {
      logSecurityEvent('api_request_forbidden', { requestId, url: new URL(url).pathname });
    } else if (response.status === 401) {
      logSecurityEvent('api_request_unauthorized', { requestId, url: new URL(url).pathname });
    }

    return response;
  } catch (error) {
    logSecurityEvent('api_request_failed', {
      requestId,
      url: new URL(url).pathname,
      error: String(error)
    });
    throw error;
  }
};

/**
 * Sanitize HTML content using secure DOMParser approach with strict allowlist
 */
export const sanitizeHTML = (html: string): string => {
  // Input validation
  if (!html || typeof html !== 'string') {
    return '';
  }

  // Use DOMParser instead of innerHTML to avoid XSS
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  
  // Check for parser errors
  const parserErrors = doc.querySelectorAll('parsererror');
  if (parserErrors.length > 0) {
    logSecurityEvent('html_parsing_error', { html: html.substring(0, 100) });
    return '';
  }
  
  // Get the body element from parsed document
  const temp = doc.body || doc.documentElement;
  
  if (!temp) {
    return '';
  }

  // Define allowlist of safe tags
  const allowedTags = new Set([
    'p', 'div', 'span', 'br', 'strong', 'em', 'b', 'i', 'u',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li',
    'a', 'img',
    'blockquote', 'code', 'pre'
  ]);

  // Define allowlist of safe attributes per tag
  const allowedAttributes = new Map([
    ['a', new Set(['href', 'title', 'target'])],
    ['img', new Set(['src', 'alt', 'title', 'width', 'height'])],
    ['*', new Set(['class', 'id'])] // Global safe attributes
  ]);

  // Remove all dangerous tags first
  const dangerousTags = ['script', 'style', 'object', 'embed', 'form', 'input', 'textarea', 'button', 'select', 'option', 'iframe', 'frame', 'frameset', 'meta', 'link', 'base'];
  dangerousTags.forEach(tagName => {
    const elements = temp.querySelectorAll(tagName);
    elements.forEach(element => element.remove());
  });

  // Process all remaining elements
  const allElements = temp.querySelectorAll('*');
  allElements.forEach(element => {
    const tagName = element.tagName.toLowerCase();
    
    // Remove non-allowlisted tags
    if (!allowedTags.has(tagName)) {
      element.remove();
      return;
    }

    // Clean attributes
    const attributes = Array.from(element.attributes);
    const tagAllowedAttrs = allowedAttributes.get(tagName) || new Set();
    const globalAllowedAttrs = allowedAttributes.get('*') || new Set();
    
    attributes.forEach(attr => {
      const attrName = attr.name.toLowerCase();
      
      // Remove event handlers
      if (attrName.startsWith('on')) {
        element.removeAttribute(attr.name);
        return;
      }
      
      // Remove dangerous attributes
      const dangerousAttrs = ['javascript:', 'vbscript:', 'data:', 'mocha:', 'livescript:'];
      if (attr.value && dangerousAttrs.some(dangerous => attr.value.toLowerCase().includes(dangerous))) {
        element.removeAttribute(attr.name);
        return;
      }
      
      // Keep only allowlisted attributes
      if (!tagAllowedAttrs.has(attrName) && !globalAllowedAttrs.has(attrName)) {
        element.removeAttribute(attr.name);
        return;
      }
      
      // Validate URL attributes
      if (['href', 'src'].includes(attrName)) {
        const value = attr.value;
        if (value && !isValidURL(value)) {
          element.removeAttribute(attr.name);
        }
      }
    });
  });

  // Log sanitization event
  logSecurityEvent('html_sanitized', {
    originalLength: html.length,
    sanitizedLength: temp.innerHTML.length,
    removed: html.length - temp.innerHTML.length
  });

  // Return the sanitized HTML content
  return temp.innerHTML;
};

/**
 * Validate URL for security
 */
const isValidURL = (url: string): boolean => {
  try {
    const parsedURL = new URL(url);
    return ['https:', 'http:', 'mailto:'].includes(parsedURL.protocol);
  } catch {
    return false;
  }
};

/**
 * Generate Content Security Policy nonce
 */
export const generateCSPNonce = (): string => {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array));
};

/**
 * Apply nonce to script/style elements
 */
export const applyNonceToElement = (element: HTMLElement, nonce: string): void => {
  if (element.tagName === 'SCRIPT' || element.tagName === 'STYLE') {
    element.setAttribute('nonce', nonce);
  }
};

/**
 * Validate Content Security Policy compliance
 */
export const validateCSPCompliance = (element: HTMLElement): boolean => {
  const config = getSecurityConfig();
  
  if (element.tagName === 'SCRIPT') {
    const src = element.getAttribute('src');
    if (src) {
      // Check if script source is allowed
      return config.csp['script-src'].some(source => {
        if (source === "'self'" && src.startsWith('/')) return true;
        if (source.startsWith('https://') && src.startsWith(source)) return true;
        return false;
      });
    } else {
      // Inline script - must have nonce
      const nonce = element.getAttribute('nonce');
      return nonce !== null && config.csp['script-src'].includes(`'nonce-${nonce}'`);
    }
  }
  
  if (element.tagName === 'STYLE') {
    const href = element.getAttribute('href');
    if (href) {
      // Check if stylesheet source is allowed
      return config.csp['style-src'].some(source => {
        if (source === "'self'" && href.startsWith('/')) return true;
        if (source.startsWith('https://') && href.startsWith(source)) return true;
        return false;
      });
    } else {
      // Inline style - must have nonce
      const nonce = element.getAttribute('nonce');
      return nonce !== null && config.csp['style-src'].includes(`'nonce-${nonce}'`);
    }
  }
  
  return true;
};

/**
 * Monitor for CSP violations
 */
export const setupCSPViolationReporting = (): void => {
  document.addEventListener('securitypolicyviolation', (event) => {
    logSecurityEvent('csp_violation', {
      directive: event.violatedDirective,
      blockedURI: event.blockedURI,
      documentURI: event.documentURI,
      effectiveDirective: event.effectiveDirective,
      originalPolicy: event.originalPolicy,
      disposition: event.disposition
    });
  });
};

/**
 * Validate input data for XSS prevention
 */
export const validateInput = (input: string, maxLength: number = 10000): { isValid: boolean; sanitized: string; error?: string } => {
  if (typeof input !== 'string') {
    return { isValid: false, sanitized: '', error: 'Input must be a string' };
  }
  
  if (input.length > maxLength) {
    return { isValid: false, sanitized: '', error: `Input exceeds maximum length of ${maxLength}` };
  }
  
  // Check for potential XSS patterns
  const xssPatterns = [
    /<script[^>]*>.*?<\/script>/gi,
    /javascript:/gi,
    /vbscript:/gi,
    /onload=/gi,
    /onerror=/gi,
    /onclick=/gi,
    /<iframe[^>]*>.*?<\/iframe>/gi,
  ];
  
  const hasXSS = xssPatterns.some(pattern => pattern.test(input));
  if (hasXSS) {
    logSecurityEvent('xss_attempt_detected', { 
      inputLength: input.length,
      suspiciousPatterns: xssPatterns.filter(pattern => pattern.test(input)).length
    });
    return { isValid: false, sanitized: '', error: 'Potentially malicious content detected' };
  }
  
  // Basic sanitization
  const sanitized = input
    .replace(/[<>]/g, '') // Remove angle brackets
    .trim();
  
  return { isValid: true, sanitized };
};

/**
 * Rate limiting utility
 */
class RateLimiter {
  private requests = new Map<string, number[]>();

  public isAllowed(identifier: string, maxRequests: number, windowMs: number): boolean {
    const now = Date.now();
    const userRequests = this.requests.get(identifier) || [];
    
    // Remove old requests outside the window
    const validRequests = userRequests.filter(timestamp => now - timestamp < windowMs);
    
    if (validRequests.length >= maxRequests) {
      logSecurityEvent('rate_limit_exceeded', { 
        identifier, 
        requestCount: validRequests.length, 
        maxRequests,
        windowMs 
      });
      return false;
    }
    
    // Add current request
    validRequests.push(now);
    this.requests.set(identifier, validRequests);
    
    return true;
  }

  public getRemainingRequests(identifier: string, maxRequests: number, windowMs: number): number {
    const now = Date.now();
    const userRequests = this.requests.get(identifier) || [];
    const validRequests = userRequests.filter(timestamp => now - timestamp < windowMs);
    
    return Math.max(0, maxRequests - validRequests.length);
  }
}

export const rateLimiter = new RateLimiter();

/**
 * Safely set HTML content using DOMParser to prevent XSS
 */
export const safeSetHTML = (element: HTMLElement, html: string): void => {
  if (!element || !html) {
    return;
  }
  
  const sanitizedHTML = sanitizeHTML(html);
  
  // Clear the element first
  element.textContent = '';
  
  // Use DOMParser to create safe DOM nodes
  const parser = new DOMParser();
  const doc = parser.parseFromString(sanitizedHTML, 'text/html');
  const body = doc.body;
  
  if (body) {
    // Move all child nodes from the parsed body to the target element
    while (body.firstChild) {
      element.appendChild(body.firstChild);
    }
  }
  
  logSecurityEvent('safe_html_set', {
    originalLength: html.length,
    sanitizedLength: sanitizedHTML.length,
    targetElement: element.tagName.toLowerCase()
  });
};

/**
 * Safely create DOM element with text content
 */
export const safeCreateElement = (tagName: string, textContent?: string, attributes?: Record<string, string>): HTMLElement => {
  const element = document.createElement(tagName);
  
  if (textContent) {
    element.textContent = textContent; // textContent is safe from XSS
  }
  
  if (attributes) {
    Object.entries(attributes).forEach(([key, value]) => {
      // Validate attribute names and values
      if (key.startsWith('on') || key.toLowerCase().includes('script')) {
        logSecurityEvent('blocked_dangerous_attribute', { attribute: key, value });
        return;
      }
      element.setAttribute(key, value);
    });
  }
  
  return element;
};

/**
 * Initialize security utilities
 */
export const initializeSecurity = (): void => {
  // Apply security headers
  applySecurityHeaders();
  
  // Setup CSP violation reporting
  setupCSPViolationReporting();
  
  // Log security initialization
  logSecurityEvent('security_initialized', {
    userAgent: navigator.userAgent,
    environment: import.meta.env.PROD ? 'production' : 'development',
    timestamp: new Date().toISOString()
  });
};

export default {
  applySecurityHeaders,
  validateRequestURL,
  secureFetch,
  sanitizeHTML,
  safeSetHTML,
  safeCreateElement,
  generateCSPNonce,
  validateCSPCompliance,
  validateInput,
  rateLimiter,
  initializeSecurity
};